import React, { useState } from "react";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UpdateVendorArgs } from "../data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import { Button, Modal, SelectMenuDropdown, Slideout, TextInput } from "@truevault/aquilo";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { CONTRACTOR_CATEGORY } from "../../types/Vendor";
import { Form } from "react-final-form";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { SelectMenuStandard } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { LEGACY_CONTRACTOR_CATEGORY_SENTINEL } from "../../util/vendors";

export function UncategorizedContractorsTable({
  orgId,
  contractors,
  refreshVendors,
  removeVendor,
  markAsCategorized,
}: {
  orgId: string;
  contractors: OrganizationDataRecipientDto[];
  refreshVendors: () => void;
  removeVendor: (vendor: OrganizationDataRecipientDto) => void;
  markAsCategorized: (dataRecipientId: string) => void;
}) {
  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(orgId, vendorId, vendorSettings);
    },
    onSuccess: refreshVendors,
  });

  return (
    <div className="border border-solid border-gray-200 rounded-lg max-w-[825px]">
      <table>
        <colgroup>
          <col style={{ width: "387px" }} />
          <col style={{ width: "378px" }} />
          <col style={{ width: "60px" }} />
        </colgroup>
      </table>
      <thead>
        <tr>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tl-lg pl-6 text-left text-xs font-medium text-gray-500 uppercase"
          >
            Contractor Name
          </th>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-left text-xs font-medium text-gray-500 uppercase"
          >
            Category
          </th>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tr-lg text-center text-xs font-medium text-gray-500 uppercase"
          >
            &nbsp;
          </th>
        </tr>
      </thead>
      <tbody>
        {contractors.map((dataRecipient) => {
          return (
            <ContractorRowWithSidebar
              key={dataRecipient.id}
              dataRecipient={dataRecipient}
              updateDataRecipient={updateDataRecipient}
              markAsCategorized={markAsCategorized}
              removeVendor={removeVendor}
              openSidebar={() => setSidebarOpenForVendorId(dataRecipient.vendorId)}
              isSidebarOpen={sidebarOpenForVendorId === dataRecipient.vendorId}
              closeSidebar={() => setSidebarOpenForVendorId(null)}
            />
          );
        })}
      </tbody>
    </div>
  );
}

function ContractorRowWithSidebar({
  dataRecipient,
  updateDataRecipient,
  markAsCategorized,
  removeVendor,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  updateDataRecipient: (dataRecipient: UpdateVendorArgs) => void;
  markAsCategorized: (dataRecipientId: string) => void;
  removeVendor: (dataRecipient: OrganizationDataRecipientDto) => void;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
}) {
  const [name, setName] = useState(dataRecipient.name);
  const [category, setCategory] = useState(dataRecipient.category);

  const dataRecipientWithLocalChanges = {
    ...dataRecipient,
    name,
    category,
  };

  const [showContractorRemoveConfirmationModal, setShowContractorRemoveConfirmationModal] =
    useState(false);

  let onCategorySelectChange = async (newCategory: string) => {
    setCategory(newCategory);

    await updateDataRecipient({
      vendorId: dataRecipientWithLocalChanges.vendorId,
      vendorSettings: {
        ...dataRecipientWithLocalChanges,
        category: newCategory,
        classificationId: dataRecipientWithLocalChanges.classification.id,
      },
    });

    markAsCategorized(dataRecipientWithLocalChanges.id);
  };

  return (
    <>
      <tr
        key={dataRecipientWithLocalChanges.id}
        className="bg-white border-t border-solid border-gray-200"
      >
        <td className="px-6 py-3.5 w-[387px] border-b-gray-200">
          <div className="flex items-center gap-3">
            <LetterInColoredBox letter={dataRecipientWithLocalChanges.name[0]} />
            <span className="text-[#111827] font-medium text-sm">
              {dataRecipientWithLocalChanges.name}
            </span>
          </div>
        </td>
        <td className="w-[378px] max-w-[378px] break-all border-b-gray-200">
          <SelectMenuDropdown<string>
            items={Object.values(CONTRACTOR_CATEGORY).map((c) => ({
              label: c,
              value: c,
              isSelected: c === category,
            }))}
            onSelect={onCategorySelectChange}
            noSelectedErrorMessage="Select Category"
            dropdownWidthPx={359}
            rightPositionPx={-220}
          />
        </td>
        <td className="w-[60px] max-w-[60px] border-b-gray-200">
          <ArrowRightCircleIcon
            className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
            onClick={openSidebar}
          />
        </td>
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          <div className="bg-neutral-0 p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-primary-900 text-lg font-bold">Contractor Settings</h3>
              <div className="cursor-pointer" onClick={closeSidebar}>
                <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
              </div>
            </div>
          </div>
          <div>
            <Form
              onSubmit={() => {}}
              render={() => {
                return (
                  <>
                    <div className="p-6">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Contractor Name
                        </label>
                        <TextInput
                          field="contractor-name"
                          onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                            let newName = e.target.value;

                            // If they set an empty name, set it to the original name and don't update
                            if (newName.length == 0) {
                              setName(dataRecipientWithLocalChanges.name);
                              return;
                            }

                            setName(newName);

                            await updateDataRecipient({
                              vendorId: dataRecipientWithLocalChanges.vendorId,
                              vendorSettings: {
                                ...dataRecipientWithLocalChanges,
                                name: newName,
                                classificationId: dataRecipientWithLocalChanges.classification.id,
                              },
                            });
                          }}
                          defaultValue={name}
                          maxLength={90}
                        />
                      </div>
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Category
                        </label>
                        <SelectMenuStandard
                          items={Object.values(CONTRACTOR_CATEGORY).map((c) => ({
                            label: c,
                            value: c,
                          }))}
                          selectedValue={
                            category !== LEGACY_CONTRACTOR_CATEGORY_SENTINEL ? category : null
                          }
                          onSelect={onCategorySelectChange}
                          placeholder="Select Category"
                        />
                      </div>
                      <div className="flex justify-center mt-6">
                        <Button
                          color="warning"
                          onClick={() => {
                            setShowContractorRemoveConfirmationModal(true);
                          }}
                        >
                          Remove Contractor
                        </Button>
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
          {showContractorRemoveConfirmationModal && (
            <Modal
              alignText="center"
              firstButton="Cancel"
              firstButtonColor="tertiary"
              onFirstButtonClick={() => {
                setShowContractorRemoveConfirmationModal(false);
              }}
              secondButton="Remove Contractor"
              secondButtonColor="warning"
              onSecondButtonClick={() => {
                removeVendor(dataRecipientWithLocalChanges);
              }}
              showOverlay
              onClose={() => {
                setShowContractorRemoveConfirmationModal(false);
              }}
              widthButton="full"
              alignButton="right"
            >
              <div className="flex items-center justify-center">
                <div className="w-[48px] h-[48px] bg-red-50 rounded-3xl p-2.5">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
                </div>
              </div>
              <div className="text-center text-gray-900 font-medium text-lg mt-2">
                Are you sure you want to remove {name}?
              </div>
              <p className="text-center text-gray-500 text-sm font-normal mt-2">
                You won't be able to undo this action and all data related to this contractor will
                be deleted.
              </p>
            </Modal>
          )}
        </>
      </Slideout>
    </>
  );
}

const LetterInColoredBox = ({ letter }: { letter: string }) => {
  const backgroundColors = [
    "bg-yellow-300",
    "bg-green-300",
    "bg-secondary-300",
    "bg-red-300",
    "bg-yellow-300",
  ];

  // pick a color based on the mod of the letter
  let bgColorForLetter = backgroundColors[letter.charCodeAt(0) % backgroundColors.length];

  return (
    <div
      className={`text-white ${bgColorForLetter} rounded-[3px] flex items-center justify-center w-[24px] h-[24px]`}
    >
      {letter.toUpperCase()}
    </div>
  );
};
